import {Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Text} from 'src/design/atoms/Text';
import {IconInfoCircle} from '@tabler/icons-react';
import React, {useState} from 'react';
import {Link, useParams, useSearchParams} from 'react-router-dom';
import {
  EntityIdentifierEnum,
  FeatureFlagEnum,
  FormStateEnum,
  ModuleEnum,
  NotificationModuleEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {FormsOfflineBoundary} from 'src/app/forms/FormsOfflineBoundary';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {NavAnchor} from 'src/design/atoms/NavAnchor';
import {FormHeaderMeta} from 'src/app/forms/FormHeaderMeta';
import {FormReferenceImageSection} from 'src/app/forms/FormReferenceImageSection';
import {DemonstrationContextBanner} from 'src/app/training/DemonstrationContextBanner';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {SplitPane} from 'src/design/atoms/SplitPane';
import {SidebarLinkForms} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {NotificationsModule} from 'src/app/notifications/NotificationsModule';
import {ViewLinkedWorkflowSection} from 'src/app/workflows/ViewLinkedWorkflowSection';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppSelector} from 'src/redux/hooks';
import {useGetFormPdfMutation} from 'src/redux/queries/form';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useFormPermissions} from 'src/shared/auth/permissions/useFormPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {FormViewType} from 'src/shared/constants/ViewTypes';
import {useDocumentDownload} from 'src/shared/hooks/useDocumentDownload';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import {useFormsBreadcrumbs} from 'src/shared/hooks/useFormsBreadcrumbs';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import useSkipTokensByFeature from 'src/shared/hooks/useSkipTokensByFeature';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {ApiEndpoints} from 'src/shared/utils/ApiCaller';
import {capitalize} from 'src/shared/utils/formatters';
import {isValidArray} from 'src/utilities/objectUtils';
import {getFormPillConfig} from '../../../../../app/forms/formHelpers';
import {useFormSubmissionViewer} from '../../../../../app/forms/hooks/useFormSubmissionViewer';
import {ActionsTab} from './components/ActionsTab/ActionsTab';
import {PackageFormModal} from './components/PackageFormModal';
import {RelatedApprovalsBanner} from './components/RelatedApprovalsBanner';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';

const ViewFormPage: React.FC = () => {
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();

  const auditPerms = useAuditLogPermissions();
  const formPerms = useFormPermissions();
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);

  const {formSubmissionId, formDefinitionId, view} = useParams();
  const fromTabParam = searchParams.get('from');
  const fromTab: 'user' | 'all' | undefined =
    fromTabParam === 'all'
      ? 'all'
      : fromTabParam === 'user'
      ? 'user'
      : undefined;

  const {download} = useDocumentDownload();
  const [getFormPdf, {isLoading: isDownloadingPdf}] = useGetFormPdfMutation();

  const {skipActions} = useSkipTokensByFeature();

  const {accessToken} = useAppSelector(state => state.auth);

  const {
    form,
    formDefinition,
    formData,
    formMeta,
    formReferenceImage,
    isLoading: isFormLoading,
    stateMachine: machine,
    isMachineLoading,
    canEdit,
    refetch: getForm,
  } = useFormSubmissionViewer({
    formDefinitionId: formDefinitionId!,
    formSubmissionId: formSubmissionId!,
  });

  useSetDocumentTitle(capitalize(formDefinition?.name));

  const {breadcrumbs, fallbackTo} = useFormsBreadcrumbs({
    mode: 'form-view',
    formDefinitionId,
    formDefinitionName: form?.formDefinition?.name,
    formIdentifier: form?.internalIdentifier,
    fromTab,
  });
  const {returnTo} = useBackNavigation(fallbackTo);

  const handleEdit = () =>
    navigate(
      PathUtility.FormEdit(
        formDefinitionId as string,
        formSubmissionId as string
      )
    );

  const handleDownloadPdf = (id: number) =>
    download(
      timeZone => getFormPdf({id, timeZone}).unwrap(),
      `form-${form?.internalIdentifier ?? id}.pdf`
    );

  const handleTransitionComplete = () => {
    const taskAssignmentId = form?.metadata?.taskAssignmentId;
    if (taskAssignmentId) {
      returnTo(
        PathUtility.ViewTaskAssignment(TaskTypeEnum.Form, taskAssignmentId)
      );
    } else {
      void getForm();
    }
  };

  const extensionTabs = useExtensionTabs(ModuleEnum.Forms, form?.id ?? 0);

  const pillOptions = getFormPillConfig(
    form?.state ?? FormStateEnum.Draft,
    form?.internalIdentifier
  );

  const formName = form?.formDefinition
    ? `${form?.formDefinition?.name}`
    : 'Form not found';

  const formMetaContent = (
    <FormHeaderMeta
      isAnonymousSubmission={
        form?.formDefinition?.metadata?.anonymousSubmission === true
      }
      creatorLastName={form?.creator?.lastName}
      creatorFirstName={form?.creator?.firstName}
      updatedAt={form?.updatedAt?.toString()}
    />
  );

  const {RenderedTabsComponent} = useUrlQueryTabV2<FormViewType>({
    view: (view ?? '') as FormViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <>
            <SplitPane pane={<ViewLinkedWorkflowSection formId={form?.id} />}>
              <RelatedApprovalsBanner formId={form?.id} />
              <FormViewComponentV2 formData={formData} formMeta={formMeta} />

              {machine && form && formData && (
                <Box pt="lg">
                  {form?.parentId !== null && form?.parentId !== undefined ? (
                    <Alert
                      color="yellow"
                      variant="light"
                      title="Unable to modify form">
                      This form was created as an embedded form within another
                      form. To edit this form's content, please edit the parent
                      form.
                      <Box pt="lg">
                        <Button
                          onClick={() =>
                            navigate(
                              PathUtility.FormSubmission(
                                form?.parent?.formDefinition.id as number,
                                form?.parentId as number,
                                'content'
                              )
                            )
                          }
                          size="sm"
                          variant="outline">
                          Go to Parent Form
                        </Button>
                      </Box>
                    </Alert>
                  ) : (
                    <FormTransitionBar
                      stateMachine={machine}
                      stateId={form?.state ?? FormStateEnum.Invalid}
                      transitionBaseUrl={`${ApiEndpoints.getStaticForm}/${formSubmissionId}/transition`}
                      onRefreshTransition={handleTransitionComplete}
                      invalidateTagsOnComplete={['Forms', 'TaskAssignments']}
                    />
                  )}
                </Box>
              )}
            </SplitPane>
          </>
        ),
      },
      {
        view: 'actions',
        label: 'Actions',
        disabled: skipActions,
        component: <>{form?.id && <ActionsTab formId={form.id} />}</>,
      },
      {
        view: 'notifications',
        label: 'Notifications',
        component: (
          <>
            <NotificationsModule
              entityId={form?.id}
              notificationType={NotificationModuleEnum.Form}
            />
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: !auditPerms.view,
        component: form?.id && (
          <AuditModule
            entityId={String(form.id)}
            entityType={EntityIdentifierEnum.Form}
          />
        ),
      },
      ...extensionTabs,
    ],
    onSelectView: view =>
      navigate(
        PathUtility.FormSubmission(
          formDefinitionId ?? 0,
          formSubmissionId ?? 0,
          view as FormViewType
        ),
        {replace: true}
      ),
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Forms}>
      {!isFormLoading && !isMachineLoading && form ? (
        <Box style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
          <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
          <PageHeader>
            <PageHeader.Title icon={SidebarLinkForms.icon}>
              {formName}
            </PageHeader.Title>
            {pillOptions.length > 0 && (
              <PageHeader.Badges>
                {pillOptions.map(pill => (
                  <PageHeader.Badge
                    key={pill.text}
                    color={pill.color}
                    variant="light">
                    {pill.text}
                  </PageHeader.Badge>
                ))}
              </PageHeader.Badges>
            )}
            <PageHeader.Meta>{formMetaContent}</PageHeader.Meta>
            <PageHeader.Actions>
              {canEdit && (
                <PageHeader.Action variant="edit" onClick={handleEdit}>
                  Edit
                </PageHeader.Action>
              )}
              <PageHeader.Action
                variant="export"
                onClick={() => handleDownloadPdf(form.id)}
                loading={isDownloadingPdf}>
                Download PDF
              </PageHeader.Action>
              {formPerms.packageExport && (
                <PageHeader.Action
                  variant="export"
                  onClick={() => setIsPackageModalOpen(true)}>
                  Package
                </PageHeader.Action>
              )}
            </PageHeader.Actions>
          </PageHeader>

          {form?.analyticSafetyEvents &&
            form?.analyticSafetyEvents.length > 0 && (
              <Alert
                variant="light"
                mb="sm"
                color="blue"
                title="Linked Safety Event"
                icon={<IconInfoCircle />}>
                <Text size="sm" mb="md">
                  This Safety Event is linked to a safety event submission.
                </Text>

                {form?.analyticSafetyEvents.map(safetyEvent => {
                  return (
                    <NavAnchor
                      key={safetyEvent.id}
                      component={Link}
                      text={`Open Safety Event ${
                        safetyEvent.internalIdentifier != null
                          ? `(${safetyEvent.internalIdentifier})`
                          : ''
                      }`}
                      to={PathUtility.SafetyEventView(
                        safetyEvent.id,
                        'content'
                      )}
                      target="_blank"
                    />
                  );
                })}
              </Alert>
            )}

          <FormReferenceImageSection
            imageUrl={formReferenceImage}
            accessToken={accessToken}
          />

          <DemonstrationContextBanner
            taskAssignmentId={form?.metadata?.taskAssignmentId ?? undefined}
          />

          {RenderedTabsComponent}

          {form?.id && (
            <PackageFormModal
              opened={isPackageModalOpen}
              onClose={() => setIsPackageModalOpen(false)}
              formId={form.id}
              formIdentifier={form.internalIdentifier}
            />
          )}
        </Box>
      ) : (
        <TableBodyRender
          isLoading={isFormLoading || isMachineLoading}
          hasData={isValidArray(formData)}
        />
      )}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.FormSubmission(
    ':formDefinitionId',
    ':formSubmissionId',
    ':view'
  ),
  element: (
    <FormsOfflineBoundary>
      <ViewFormPage />
    </FormsOfflineBoundary>
  ),
  isPrivate: true,
});

export default ViewFormPage;
